import { launchContentPack } from "@stormglass/game-content";
import { localizer } from "@stormglass/game-localization";
import type { WeaponKind } from "@stormglass/game-core";

import { RecoveryBanner } from "../components/RecoveryBanner";
import { WeatherIcon } from "../components/WeatherIcon";
import { useGameHost, useGameState } from "../game/GameContext";

interface ShopItem {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly cost: number;
  readonly unlockRound: number;
  readonly kind?: WeaponKind;
}

const WEATHER_ITEMS: readonly ShopItem[] = [
  {
    id: "weapon.rain.base",
    name: "Rain Array",
    description:
      "Create persistent cells over dense threat clusters and apply Wet.",
    cost: launchContentPack.economy.firstWeatherCost,
    unlockRound: 10,
    kind: "rain",
  },
  {
    id: "weapon.wind.base",
    name: "Pressure Vanes",
    description:
      "Drive a gust through dangerous concentrations and push them outward.",
    cost: launchContentPack.economy.firstWeatherCost,
    unlockRound: 10,
    kind: "wind",
  },
  {
    id: "weapon.hail.base",
    name: "Cryo Impactor",
    description:
      "Strike high-health targets with heavy impacts and stacking Chill.",
    cost: launchContentPack.economy.firstWeatherCost,
    unlockRound: 10,
    kind: "hail",
  },
];

export function ShopScreen(): React.JSX.Element {
  const host = useGameHost();
  const { snapshot } = useGameState();
  const defenses: ShopItem[] = Object.values(launchContentPack.defenses).map(
    (defense) => ({
      id: defense.id,
      name:
        defense.id
          .split(".")
          .at(-1)
          ?.replaceAll("-", " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase()) ?? defense.id,
      description:
        defense.kind === "shield"
          ? "Absorb a portion of incoming breach damage."
          : defense.kind === "integrity"
            ? "Increase the chamber’s permanent structural capacity."
            : "Push nearby threats outward when the chamber is struck.",
      cost: defense.cost,
      unlockRound: defense.unlockRound,
    }),
  );
  const items = [...WEATHER_ITEMS, ...defenses];
  return (
    <div className="shop-page page-panel">
      <RecoveryBanner />
      <div className="page-heading">
        <div>
          <p className="eyebrow">PERMANENT SYSTEMS</p>
          <h1>{localizer.t("shop.title")}</h1>
          <p className="page-lede">{localizer.t("shop.detail")}</p>
        </div>
        <div className="currency-card">
          <small>{localizer.t("hud.condensate")}</small>
          <strong>◇ {localizer.number(snapshot.condensate)}</strong>
        </div>
      </div>
      <div className="shop-grid">
        {items.map((item) => {
          const owned =
            snapshot.permanentOwned.includes(item.id) ||
            (item.kind !== undefined &&
              snapshot.ownedWeapons.includes(item.kind));
          const accessible =
            snapshot.permanentAccess.includes(item.id) ||
            (item.kind !== undefined &&
              snapshot.permanentAccess.some((id) =>
                id.includes(item.kind ?? ""),
              )) ||
            host.getStatistics().highestRound >= item.unlockRound;
          const canBuy =
            accessible && !owned && snapshot.condensate >= item.cost;
          return (
            <article
              className={`shop-card ${owned ? "is-owned" : ""} ${accessible ? "" : "is-locked"}`}
              key={item.id}
            >
              <div className="shop-card__visual">
                {item.kind === undefined ? (
                  <span className="defense-icon">◇</span>
                ) : (
                  <WeatherIcon kind={item.kind} />
                )}
                {owned && <span className="owned-badge">ONLINE</span>}
              </div>
              <div className="shop-card__copy">
                <small>{item.kind?.toUpperCase() ?? "CHAMBER DEFENSE"}</small>
                <h2>{item.name}</h2>
                <p>{item.description}</p>
              </div>
              <div className="shop-card__action">
                {owned ? (
                  <strong>INSTALLED</strong>
                ) : accessible ? (
                  <button
                    type="button"
                    disabled={!canBuy}
                    onClick={() => {
                      host.command({ type: "purchase-permanent", id: item.id });
                    }}
                  >
                    <span>◇ {item.cost}</span>
                    <small>
                      {canBuy
                        ? "PURCHASE"
                        : `NEED ${String(Math.max(0, item.cost - snapshot.condensate))} MORE`}
                    </small>
                  </button>
                ) : (
                  <span>
                    <strong>LOCKED</strong>
                    <small>
                      {localizer.t("shop.locked", { round: item.unlockRound })}
                    </small>
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
